import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { CalendarDays, Loader2, Play, Plus, Sparkles, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { useSocialSchedulerApi } from '../../../../common/hooks/use-social-scheduler-api.hook';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { SocialAccount, SocialPlatform, SocialPost } from '../../../../common/models/social-scheduler.model';
import { CsAsset, CsProject } from '../../../../common/models/content-studio.model';
import { PostCard } from './PostCard';
import { PlatformIcon } from './PlatformIcon';

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="mb-1.5 text-sm font-medium text-muted-foreground">{children}</div>
);

export interface PostFormValues {
    selectedAssetUuids: string[];
    platform: SocialPlatform;
    accountUuid: string;
    caption: string;
    hashtags: string[];
    scheduledAt: string; // local datetime-local string
}

interface PostFormProps {
    accounts: SocialAccount[];
    assets: CsAsset[];
    /** Content Studio projects (create mode) — required to gate the asset picker. Edit mode ignores this. */
    projects?: CsProject[];
    initial?: Partial<PostFormValues>;
    /** Hide platform/account/asset pickers (for edit mode where these are fixed) */
    lockTargeting?: boolean;
    onSubmit: (values: PostFormValues, asDraft: boolean) => Promise<void>;
    submitLabel?: string;
    draftLabel?: string;
    submitting?: boolean;
}

export const PostForm: React.FC<PostFormProps> = ({
    accounts,
    assets,
    projects: projectsProp,
    initial,
    lockTargeting = false,
    onSubmit,
    submitLabel,
    draftLabel,
    submitting = false,
}) => {
    const { t } = useTranslation();
    const api = useSocialSchedulerApi();
    const csApi = useContentStudioApi();

    const [selectedAssetUuids, setSelectedAssetUuids] = useState<string[]>(initial?.selectedAssetUuids || []);
    const [platform, setPlatform] = useState<SocialPlatform>(initial?.platform || 'instagram');
    const [accountUuid, setAccountUuid] = useState(initial?.accountUuid || '');
    const [caption, setCaption] = useState(initial?.caption || '');
    const [hashtagInput, setHashtagInput] = useState('');
    const [hashtags, setHashtags] = useState<string[]>(initial?.hashtags || []);
    const [scheduledAt, setScheduledAt] = useState<Dayjs | null>(
        initial?.scheduledAt ? dayjs(initial.scheduledAt) : null,
    );
    const [generating, setGenerating] = useState(false);

    // Project picker (only used in create mode — edit mode uses the assets prop directly)
    const projects = projectsProp || [];
    const [selectedProjectUuid, setSelectedProjectUuid] = useState<string>('');
    const [projectAssets, setProjectAssets] = useState<CsAsset[]>([]);
    const [assetsLoadingForProject, setAssetsLoadingForProject] = useState(false);

    useEffect(() => {
        if (lockTargeting || !selectedProjectUuid) {
            setProjectAssets([]);
            return;
        }
        let cancelled = false;
        setAssetsLoadingForProject(true);
        (async () => {
            try {
                const res = await csApi.listAssets({ projectUuid: selectedProjectUuid, status: 'ready', limit: 100 });
                if (cancelled) return;
                setProjectAssets((res as any).items || []);
            } catch (err) {
                console.error('Failed to load assets for project', err);
                if (!cancelled) setProjectAssets([]);
            } finally {
                if (!cancelled) setAssetsLoadingForProject(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectUuid, lockTargeting]);

    // In edit mode, use the assets prop. In create mode, use assets fetched for the selected project.
    const visibleAssets = lockTargeting ? assets : projectAssets;

    const selectedAssetUuid = selectedAssetUuids[0] || '';

    // Only show platforms that have at least one active account
    const availablePlatforms = [...new Set(
        accounts.filter((a) => a.status === 'active').map((a) => a.platform),
    )] as SocialPlatform[];

    const filteredAccounts = accounts.filter((a) => a.platform === platform && a.status === 'active');

    // Auto-select first available platform if current one has no active account
    useEffect(() => {
        if (lockTargeting) return;
        if (availablePlatforms.length > 0 && !availablePlatforms.includes(platform)) {
            setPlatform(availablePlatforms[0]);
        }
    }, [availablePlatforms, platform, lockTargeting]);

    // Auto-select account when platform changes (only in create mode)
    useEffect(() => {
        if (lockTargeting) return;
        const match = accounts.find((a) => a.platform === platform && a.status === 'active');
        setAccountUuid(match?.uuid || '');
    }, [platform, accounts, lockTargeting]);

    const addHashtag = () => {
        const tag = hashtagInput.trim().replace(/^#/, '');
        if (tag && !hashtags.includes(tag)) {
            setHashtags([...hashtags, tag]);
        }
        setHashtagInput('');
    };

    const removeHashtag = (tag: string) => {
        setHashtags(hashtags.filter((h) => h !== tag));
    };

    const handleGenerateCaption = () => {
        if (!selectedAssetUuid) return;
        setGenerating(true);
        api.generateCaptionStream(
            { assetUuid: selectedAssetUuid, platform },
            (event) => {
                if (event.type === 'tool_result' || event.type === 'done') {
                    setGenerating(false);
                    if (event.data?.caption) {
                        const result = event.data.caption;
                        if (result.caption) setCaption(result.caption);
                        if (result.hashtags?.length) setHashtags(result.hashtags);
                    }
                }
            },
        );
    };

    const canSubmit = selectedAssetUuid && accountUuid && scheduledAt?.isValid();

    const handleSubmit = (asDraft: boolean) => {
        if (!canSubmit || !scheduledAt) return;
        onSubmit(
            {
                selectedAssetUuids,
                platform,
                accountUuid,
                caption,
                hashtags,
                scheduledAt: scheduledAt.format('YYYY-MM-DDTHH:mm'),
            },
            asDraft,
        );
    };

    // ── Date/time picker helpers ────────────────────────────────
    const hasValidDate = !!scheduledAt?.isValid();

    const handleDaySelect = (day: Date | undefined) => {
        if (!day) return;
        // Keep the already-chosen time when re-picking the date; default to noon.
        const base = hasValidDate ? scheduledAt! : dayjs().hour(12).minute(0);
        setScheduledAt(dayjs(day).hour(base.hour()).minute(base.minute()));
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const [h, m] = e.target.value.split(':').map(Number);
        if (Number.isNaN(h)) return;
        const base = hasValidDate ? scheduledAt! : dayjs();
        setScheduledAt(base.hour(h).minute(Number.isNaN(m) ? 0 : m));
    };

    // ── Live preview shape ──────────────────────────────────────
    const previewAccount = accounts.find((a) => a.uuid === accountUuid);
    const previewAssetMap = useMemo(() => {
        const url: Record<string, string> = {};
        const kind: Record<string, 'image' | 'video' | 'upload'> = {};
        const source = visibleAssets.length > 0 ? visibleAssets : assets;
        for (const a of source) {
            if (a.url) url[a.uuid] = a.url;
            if (a.kind) kind[a.uuid] = a.kind;
        }
        return { url, kind };
    }, [visibleAssets, assets]);

    const previewPost: SocialPost = {
        uuid: 'preview',
        vendor: '',
        assetUuid: selectedAssetUuids[0] || '',
        assetUuids: selectedAssetUuids,
        platform,
        accountUuid,
        caption,
        hashtags,
        linkedProductUuids: [],
        scheduledAt: (scheduledAt?.isValid() ? scheduledAt.toISOString() : new Date().toISOString()),
        status: 'scheduled',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };

    const showLivePreview = !!selectedAssetUuid;

    return (
        <div className="flex flex-wrap items-start gap-6 md:flex-nowrap">
        <div className="flex min-w-0 flex-1 flex-col gap-5 rounded-xl border bg-card p-6">
            {/* Platform */}
            {!lockTargeting && (
                <div>
                    <FieldLabel>{t('SOCIAL_SCHEDULER.PLATFORM', 'Platform')}</FieldLabel>
                    <Select
                        value={availablePlatforms.includes(platform) ? platform : undefined}
                        onValueChange={(v) => setPlatform(v as SocialPlatform)}
                        disabled={availablePlatforms.length === 0}
                    >
                        <SelectTrigger className="h-9 w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {availablePlatforms.map((p) => (
                                <SelectItem key={p} value={p}>
                                    <span className="flex items-center gap-2">
                                        <PlatformIcon platform={p} size={18} />
                                        {p.charAt(0).toUpperCase() + p.slice(1)}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {availablePlatforms.length === 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">
                            {t('SOCIAL_SCHEDULER.NO_ACTIVE_ACCOUNTS', 'No active accounts. Connect an account first.')}
                        </p>
                    )}
                </div>
            )}

            {/* Account */}
            {!lockTargeting && (
                <div>
                    <FieldLabel>{t('SOCIAL_SCHEDULER.ACCOUNT', 'Account')}</FieldLabel>
                    <Select
                        value={accountUuid || undefined}
                        onValueChange={setAccountUuid}
                        disabled={filteredAccounts.length === 0}
                    >
                        <SelectTrigger className="h-9 w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {filteredAccounts.map((a) => (
                                <SelectItem key={a.uuid} value={a.uuid}>{a.accountName}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {filteredAccounts.length === 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">
                            {t('SOCIAL_SCHEDULER.NO_ACCOUNT_FOR_PLATFORM', 'No connected account for this platform')}
                        </p>
                    )}
                </div>
            )}

            {/* Project picker (create mode only) */}
            {!lockTargeting && (
                <div>
                    <FieldLabel>{t('SOCIAL_SCHEDULER.SELECT_PROJECT', 'Pick a Content Studio project')}</FieldLabel>
                    {projects.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            {t('SOCIAL_SCHEDULER.NO_PROJECTS', 'No Content Studio projects yet. Create one first to schedule from its assets.')}
                        </p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {projects.map((p) => {
                                const isSelected = selectedProjectUuid === p.uuid;
                                return (
                                    <button
                                        key={p.uuid}
                                        type="button"
                                        onClick={() => {
                                            setSelectedProjectUuid(p.uuid);
                                            setSelectedAssetUuids([]);
                                        }}
                                        aria-pressed={isSelected}
                                        className={cn(
                                            'max-w-[240px] truncate rounded-lg border px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                                            isSelected
                                                ? 'border-foreground bg-accent'
                                                : 'border-input bg-background hover:bg-accent',
                                        )}
                                    >
                                        {p.name}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Asset picker — only after a project is selected (create mode) or always (edit mode) */}
            {(lockTargeting || selectedProjectUuid) && (
                <div>
                    <FieldLabel>
                        {t('SOCIAL_SCHEDULER.SELECT_ASSETS', 'Select assets from Content Studio (click to add, click again to remove)')}
                    </FieldLabel>
                    {assetsLoadingForProject ? (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Loader2 className="size-3.5 animate-spin" />
                            {t('SOCIAL_SCHEDULER.LOADING_ASSETS', 'Loading assets…')}
                        </div>
                    ) : visibleAssets.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            {lockTargeting
                                ? t('SOCIAL_SCHEDULER.NO_ASSETS', 'No ready assets. Generate some in Content Studio first.')
                                : t('SOCIAL_SCHEDULER.NO_ASSETS_IN_PROJECT', 'No ready assets in this project yet. Generate some in Content Studio first.')}
                        </p>
                    ) : (
                        <div
                            role="group"
                            aria-label={t('SOCIAL_SCHEDULER.SELECT_ASSETS', 'Select assets') || 'Select assets'}
                            className="flex max-h-60 flex-wrap gap-2 overflow-auto pe-1"
                        >
                            {visibleAssets.map((asset, idx) => {
                                const isSelected = selectedAssetUuids.includes(asset.uuid);
                                const selectionIdx = selectedAssetUuids.indexOf(asset.uuid);
                                const isVideo = asset.kind === 'video';
                                const posterUrl = asset.thumbnailUrl || (isVideo ? '' : asset.url);
                                const toggle = () => {
                                    setSelectedAssetUuids((prev) =>
                                        prev.includes(asset.uuid)
                                            ? prev.filter((u) => u !== asset.uuid)
                                            : [...prev, asset.uuid],
                                    );
                                };
                                return (
                                    <button
                                        key={asset.uuid}
                                        type="button"
                                        onClick={toggle}
                                        onKeyDown={(e: React.KeyboardEvent) => {
                                            if (e.key === ' ' || e.key === 'Enter') {
                                                e.preventDefault();
                                                toggle();
                                            }
                                        }}
                                        aria-pressed={isSelected}
                                        aria-label={
                                            isSelected
                                                ? `Asset ${idx + 1} selected (position ${selectionIdx + 1}). Press Enter to deselect.`
                                                : `Asset ${idx + 1}. Press Enter to select.`
                                        }
                                        className={cn(
                                            'relative size-20 shrink-0 overflow-hidden bg-cover bg-center p-0 transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                                            isSelected ? 'border-2 border-foreground' : 'rounded-xl border hover:border-foreground/40',
                                            isVideo ? 'bg-black' : 'bg-muted',
                                        )}
                                        style={{ backgroundImage: posterUrl ? `url(${posterUrl})` : undefined }}
                                    >
                                        {isVideo && !posterUrl && asset.url && (
                                            <video
                                                src={asset.url}
                                                muted
                                                playsInline
                                                preload="metadata"
                                                className="absolute inset-0 size-full object-cover"
                                            />
                                        )}
                                        {isVideo && (
                                            <span aria-hidden className="absolute bottom-1 start-1 flex size-[22px] items-center justify-center rounded-full bg-black/55 text-white">
                                                <Play className="size-3.5" />
                                            </span>
                                        )}
                                        {isSelected && (
                                            <span aria-hidden className="absolute end-1 top-1 flex size-5 items-center justify-center rounded-full bg-foreground text-[11px] font-medium text-background">
                                                {selectionIdx + 1}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Caption */}
            <div>
                <div className="mb-1 flex items-center justify-between">
                    <FieldLabel>{t('SOCIAL_SCHEDULER.CAPTION', 'Caption')}</FieldLabel>
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={!selectedAssetUuid || generating}
                        onClick={handleGenerateCaption}
                    >
                        <Sparkles />
                        {generating
                            ? t('SOCIAL_SCHEDULER.GENERATING', 'Generating...')
                            : t('SOCIAL_SCHEDULER.GENERATE_WITH_AI', 'Generate with AI')}
                    </Button>
                </div>
                <Textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    rows={4}
                    placeholder={t('SOCIAL_SCHEDULER.CAPTION_PLACEHOLDER', 'Write your caption...') || ''}
                />
                <div className="mt-1 text-end text-xs text-muted-foreground">
                    {caption.length}/2200
                </div>
            </div>

            {/* Hashtags — chip + inline input pattern (matches V3 AxisEditor) */}
            <div>
                <FieldLabel>{t('SOCIAL_SCHEDULER.HASHTAGS', 'Hashtags')}</FieldLabel>
                <div className="flex flex-wrap items-center gap-2">
                    {hashtags.map((h, index) => (
                        <span
                            key={`${h}-${index}`}
                            className="inline-flex items-center gap-1.5 rounded-md border bg-muted px-2 py-1 text-sm text-foreground"
                        >
                            #{h}
                            <button
                                type="button"
                                onClick={() => removeHashtag(h)}
                                aria-label={t('SOCIAL_SCHEDULER.REMOVE_HASHTAG', 'Remove hashtag')}
                                className="text-muted-foreground transition-colors hover:text-destructive"
                            >
                                <X className="size-3.5" />
                            </button>
                        </span>
                    ))}
                    <div className="flex items-center gap-2">
                        <Input
                            value={hashtagInput}
                            onChange={(e) => setHashtagInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHashtag(); } }}
                            placeholder={t('SOCIAL_SCHEDULER.ADD_HASHTAG', 'Add hashtag…') || ''}
                            className="h-9 w-[200px]"
                        />
                        <Button
                            variant="outline"
                            className="h-9"
                            onClick={addHashtag}
                            disabled={!hashtagInput.trim()}
                        >
                            <Plus />
                            {t('PAGES.NEW_PRODUCT.VARIANTS.ADD', 'Add')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Schedule date/time */}
            <div>
                <FieldLabel>{t('SOCIAL_SCHEDULER.SCHEDULE_AT', 'Schedule at')}</FieldLabel>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            className={cn('h-9 w-full justify-start font-normal', !hasValidDate && 'text-muted-foreground')}
                        >
                            <CalendarDays className="text-muted-foreground" />
                            {hasValidDate ? scheduledAt!.format('DD/MM/YYYY HH:mm') : 'DD/MM/YYYY HH:mm'}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0">
                        <Calendar
                            mode="single"
                            selected={hasValidDate ? scheduledAt!.toDate() : undefined}
                            defaultMonth={hasValidDate ? scheduledAt!.toDate() : undefined}
                            disabled={{ before: new Date() }}
                            onSelect={handleDaySelect}
                        />
                        <div className="border-t p-3">
                            <Input
                                type="time"
                                value={hasValidDate ? scheduledAt!.format('HH:mm') : ''}
                                onChange={handleTimeChange}
                                aria-label={t('SOCIAL_SCHEDULER.SCHEDULE_AT', 'Schedule at')}
                                className="h-8"
                            />
                        </div>
                    </PopoverContent>
                </Popover>
            </div>

            {/* Actions */}
            <div className="mt-2 flex justify-end gap-3">
                <Button
                    variant="outline"
                    className="h-9"
                    disabled={!canSubmit || submitting}
                    onClick={() => handleSubmit(true)}
                >
                    {draftLabel || t('SOCIAL_SCHEDULER.SAVE_DRAFT', 'Save as draft')}
                </Button>
                <Button
                    className="h-9"
                    disabled={!canSubmit || submitting}
                    onClick={() => handleSubmit(false)}
                >
                    {submitting
                        ? t('SOCIAL_SCHEDULER.SCHEDULING', 'Scheduling...')
                        : (submitLabel || t('SOCIAL_SCHEDULER.SCHEDULE_POST', 'Schedule post'))}
                </Button>
            </div>
        </div>

        {/* Live preview pane (right side) */}
        <div className="w-full flex-[1_1_100%] self-start md:sticky md:top-4 md:w-[360px] md:flex-[0_0_360px]">
            <div className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                {t('SOCIAL_SCHEDULER.LIVE_PREVIEW', 'Live preview')}
            </div>
            {showLivePreview ? (
                <PostCard
                    post={previewPost}
                    assetUrlMap={previewAssetMap.url}
                    assetKindMap={previewAssetMap.kind}
                    accountName={previewAccount?.accountName}
                    accountAvatar={previewAccount?.accountAvatar}
                />
            ) : (
                <div className="border-2 border-dashed bg-card px-6 py-10 text-center text-sm leading-relaxed text-muted-foreground">
                    {t('SOCIAL_SCHEDULER.PREVIEW_HINT', 'Pick a project, an asset and an account to see how this post will look.')}
                </div>
            )}
        </div>
        </div>
    );
};
